import { NO_POS } from './ast.ts';
import type { EvalContext, SourceFetcherCtx } from './EvalContext.ts';
import { createRootEnv } from './env.ts';
import { MaterializationError } from './errors.ts';
import { defaultOpTable, defaultVerbTable, type EvalState, evalVerbCall } from './evaluator.ts';
import type { OpCall, Spans } from './normalize.ts';
import type { NameTable } from './op-meta.ts';
import type { LogicalPlan, SourceDescriptor, TsRefBucketHint } from './plan.ts';
import { describeSchema, schemasEqual } from './schema/types.ts';
import {
  type Dataset,
  expectMaterialized,
  isLazy,
  isMaterialized,
  type LazyDataset,
  type MaterializedDataset,
} from './value.ts';

/**
 * Materialiser for `LazyDataset` — sub-task 4-prep.4. Walks a
 * `LogicalPlan` to its source, fetches it via the per-source-kind
 * fetcher registered on `EvalContext.sourceFetchers`, then re-runs
 * each `PlanNode.verb` over the materialised source by recursively
 * re-entering `evalVerbCall`. Returns the final `MaterializedDataset`
 * after a schema-equality post-condition.
 *
 * In scope (conservative shipped behaviour): source fetcher dispatch on
 * `src.kind` with a missing-fetcher diagnostic
 * (`MaterializationError('missing-fetcher', …)`); verb-chain replay via
 * `evalVerbCall` (standard arquero path; per-plan-node error positions
 * degrade to `NO_POS` since validation already ran at type-check time);
 * two memoisation layers via `session.fetch` keyed on
 * `['materialize-source', src.kind, src]` and on
 * `['materialize-plan', source, ops-without-schemas]` so plans sharing
 * a source coalesce and re-materialising the same plan is free;
 * cancellation through `EvalContext.signal` into `SourceFetcherCtx`;
 * outer-only materialisation (`LazyDataset` cells inside the result
 * remain lazy — see D5 boundaries: inner cells force at unnest, scalar
 * coercion, per-cell lift, or explicit `(materialize ds)`); a schema-
 * equality post-condition that throws
 * `MaterializationError('schema-mismatch', …)` when the verb-chain
 * output disagrees with `ds.schema`.
 *
 * Out of scope for this commit: CDF push-down (recognising compatible
 * verb chains as native CDF datapoints params) lands in a later
 * optimiser phase. Real CDF-backed fetchers: qkg waits on Phase 4a;
 * ts-ref's CDF wiring waits on `getCogniteClient`. Test code populates
 * `sourceFetchers` with stubs today.
 */
export const materialize = async (
  ds: LazyDataset,
  ctx: EvalContext,
  ops: NameTable = defaultOpTable,
  verbs: NameTable = defaultVerbTable,
): Promise<MaterializedDataset> => {
  return ctx.session.fetch(planCacheKey(ds.plan), async () => {
    const source = await fetchSource(tsRefBucketHint(ds.plan), ctx);
    const state: EvalState = { spans: emptySpans, ops, verbs, evalCtx: ctx };
    const env = createRootEnv(new Set(ops.keys()));
    const final = await ds.plan.ops.reduce<Promise<Dataset>>(async (acc, node, i) => {
      const prev = await acc;
      // Re-bind the per-cell lift axis recorded at lift time so a
      // deferred per-cell verb re-enters its per-cell form. Without
      // this, dispatch re-derives the form from `prev` (the inner
      // TS-ref seed, which has none of the outer nested-dataset
      // columns) and misdispatches to the row-level branch.
      return evalVerbCall(node.args as OpCall, prev, env, planNodePath(i, node.verb), {
        ...state,
        liftColumn: node.liftColumn,
      });
    }, Promise.resolve<Dataset>(source));
    const materialised = expectMaterialized(final, '(materialize <plan>)', NO_POS);
    if (!schemasEqual(materialised.schema, ds.schema)) {
      throw new MaterializationError(
        'schema-mismatch',
        `materialised schema disagrees with declared LazyDataset schema: actual={${describeSchema(materialised.schema)}}, declared={${describeSchema(ds.schema)}}`,
        NO_POS,
      );
    }
    return materialised;
  });
};

/**
 * Materialise a `Dataset` regardless of subtype: already-materialised
 * inputs short-circuit to a resolved promise; `LazyDataset`s route
 * through {@link materialize}. The defensive `throw` is unreachable
 * in practice — every concrete `Dataset` is either materialised or
 * lazy — but TypeScript can't infer that closed-union shape from the
 * abstract base, and a runtime error is preferable to a silent
 * `undefined` if a third subtype is ever added.
 *
 * Used by React-side hooks (`useMaterialize`, `useMaterializedExpr`)
 * so the narrow-or-throw dance lives in one place.
 */
export const materializeIfLazy = (
  ds: Dataset,
  ctx: EvalContext,
  ops: NameTable = defaultOpTable,
  verbs: NameTable = defaultVerbTable,
): Promise<MaterializedDataset> => {
  if (isMaterialized(ds)) {
    return Promise.resolve(ds);
  }
  if (isLazy(ds)) {
    return materialize(ds, ctx, ops, verbs);
  }
  throw new TypeError(`materializeIfLazy: unrecognised Dataset subtype (state=${(ds as Dataset).state})`);
};

/**
 * Push the downstream per-cell `time-bucket` intent onto a `ts-ref`
 * source so the fetcher can request the right time range (and, when it
 * maps exactly, server-side downsampling) rather than a fixed rolling
 * raw window — the fix for "1-year `:window` returns nothing because
 * only the last 90 days are fetched". Pure plan→data extraction; the
 * CDF mapping (period→granularity / agg→aggregate / raw fallback)
 * stays the fetcher's concern. No-op for non-`ts-ref` sources or a
 * plan with no `time-bucket` node.
 */
const tsRefBucketHint = (plan: LogicalPlan): SourceDescriptor => {
  const src = plan.source;
  if (src.kind !== 'ts-ref') {
    return src;
  }
  const tb = plan.ops.find((n) => n.verb === 'time-bucket')?.args as
    | { readonly period?: string; readonly window?: unknown; readonly agg?: readonly unknown[] }
    | undefined;
  if (tb === undefined) {
    return src;
  }
  const window =
    Array.isArray(tb.window) && tb.window.length === 2
      ? ([String(tb.window[0]), String(tb.window[1])] as const)
      : undefined;
  const aggs = (tb.agg ?? [])
    .map((e) => (e as { readonly $sym?: unknown }).$sym)
    .filter((s): s is string => typeof s === 'string');
  if (window === undefined && tb.period === undefined && aggs.length === 0) {
    return src;
  }
  const bucket: TsRefBucketHint = {
    window,
    period: tb.period as TsRefBucketHint['period'],
    aggs: aggs.length > 0 ? aggs : undefined,
  };
  return { ...src, bucket };
};

/**
 * Resolve a source descriptor to a `MaterializedDataset` via the
 * fetcher registered for its `kind`. Wraps the call in
 * `session.fetch(['materialize-source', kind, descriptor], …)` so
 * concurrent plans referencing the same source coalesce on a single
 * fetcher invocation (and re-runs hit cache).
 *
 * Missing fetcher → `MaterializationError('missing-fetcher')`. The
 * fetcher itself sees a narrow `SourceFetcherCtx` carrying the session
 * and signal — it must not recurse back into expression evaluation.
 */
const fetchSource = (src: SourceDescriptor, ctx: EvalContext): Promise<MaterializedDataset> => {
  const fetcher = ctx.sourceFetchers?.[src.kind];
  if (fetcher === undefined) {
    throw new MaterializationError(
      'missing-fetcher',
      `no fetcher registered on EvalContext.sourceFetchers for source kind '${src.kind}'`,
      NO_POS,
    );
  }
  const sfCtx: SourceFetcherCtx = { session: ctx.session, signal: ctx.signal };
  // `fetcher`'s parameter is `Extract<SourceDescriptor, { kind: K }>`
  // for whichever `K` the map slot stores — the lookup-by-discriminator
  // narrows correctly at runtime but TS can't follow the relationship
  // across the mapped-type indirection. `as never` is the standard
  // escape for this dispatch-table pattern (same shape as evalOpCall's
  // `parsed as never` cast).
  return ctx.session.fetch(sourceCacheKey(src), () => fetcher(src as never, sfCtx));
};

/**
 * Stable cache key for an entire `LogicalPlan`. Excludes the cached
 * `schemaAfter` field on each `PlanNode` because `Schema` is a `Map`
 * (and `Map`s don't JSON-serialise faithfully — the `Session.fetch`
 * default hash uses `JSON.stringify`); only `(verb, args)` is keying-
 * relevant since `schemaAfter` is derivable from those two via the
 * verb's `propagateSchema`. `liftColumn` IS keying-relevant: two
 * otherwise-identical plans lifted on different axes replay
 * differently, so it must not collide in the memo.
 */
const planCacheKey = (plan: LogicalPlan): readonly unknown[] => [
  'materialize-plan',
  plan.source,
  plan.ops.map(({ verb, args, liftColumn }) => ({ verb, args, liftColumn })),
];

/** Per-source cache key. Discriminator + descriptor — descriptor's
 * own structure (and any nested `AstInput` for `where`/`sort`) is
 * JSON-serialisable, so the standard `Session.fetch` hash is sound. */
const sourceCacheKey = (src: SourceDescriptor): readonly unknown[] => ['materialize-source', src.kind, src];

/**
 * Synthesised path for plan-node error attribution. Real source
 * positions were dropped when the plan was built (the original verb
 * call's `path` was call-site-relative, not plan-relative), so we
 * fabricate a path that at least identifies the offending verb and
 * its index within the plan.
 */
const planNodePath = (i: number, verb: string): string => `materialize/${i}/${verb}`;

/** Empty spans sidecar — materialiser-time errors have no source
 * positions (errors during plan replay surface with `NO_POS`). */
const emptySpans: Spans = new Map();
